/**
 * Platform overview.
 *
 * The comp leads with MRR and plan mix; neither exists in the backend, so this
 * leads with what is real and actionable — the review queue, activation
 * stragglers, and what the network actually holds. Revenue lives on Billing,
 * clearly marked as not yet wired.
 */
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Buildings,
  CheckCircle,
  Handshake,
  House,
  Prohibit,
  Tray,
  UsersThree,
} from '@phosphor-icons/react';
import type { Icon } from '@phosphor-icons/react';
import { useOverview } from '../queries/platform';
import { apiError } from '../lib/api';
import { relativeDate } from '../lib/format';
import {
  Avatar,
  Badge,
  EmptyState,
  ErrorState,
  LoadingBlock,
  StatusDot,
  type Tone,
} from '../components/ui';

function StatCard({
  icon: IconCmp,
  value,
  label,
  sub,
  tone = 'off',
  to,
}: {
  icon: Icon;
  value: number | string;
  label: string;
  sub?: string;
  tone?: Tone;
  to?: string;
}) {
  const body = (
    <>
      <div className="row" style={{ marginBottom: 14 }}>
        <div
          className="icon-tile"
          style={{
            background: `var(--${tone === 'off' ? 'inset-2' : `${tone}-bg`})`,
            color: `var(--${tone === 'off' ? 'ac' : tone})`,
          }}
        >
          <IconCmp size={19} />
        </div>
        <div className="spacer" />
        {to && <ArrowRight size={15} color="var(--faint)" />}
      </div>
      <p className="num" style={{ margin: 0, fontSize: 28, lineHeight: 1 }}>
        {value}
      </p>
      <p className="t-md strong" style={{ margin: '9px 0 0' }}>
        {label}
      </p>
      {sub && (
        <p className="t-xs faint" style={{ margin: '3px 0 0' }}>
          {sub}
        </p>
      )}
    </>
  );

  if (to) {
    return (
      <Link to={to} className="card card-pad" style={{ color: 'inherit', display: 'block' }}>
        {body}
      </Link>
    );
  }
  return <div className="card card-pad">{body}</div>;
}

export default function Overview() {
  const navigate = useNavigate();
  const { data, isPending, isError, error, refetch } = useOverview();

  if (isPending) return <LoadingBlock label="Loading the platform…" />;
  if (isError) return <ErrorState message={apiError(error)} onRetry={() => void refetch()} />;

  const { agencies, registrations, network, recentAgencies, recentRegistrations } = data;

  return (
    <div className="stack animate-up" style={{ gap: 18 }}>
      <div className="grid grid-4">
        <StatCard
          icon={Tray}
          value={registrations.open}
          label="Awaiting review"
          sub={`${registrations.new} new · ${registrations.reviewing} in review`}
          tone={registrations.open > 0 ? 'warn' : 'ok'}
          to="/registrations"
        />
        <StatCard
          icon={Buildings}
          value={agencies.total}
          label="Total agencies"
          sub={`${agencies.active} active · ${agencies.inactive} suspended`}
          to="/agencies"
        />
        <StatCard
          icon={House}
          value={network.properties.toLocaleString('en-IN')}
          label="Properties listed"
          sub={`${network.customers.toLocaleString('en-IN')} clients · ${network.deals.toLocaleString('en-IN')} deals`}
        />
      </div>

      <div className="grid" style={{ gridTemplateColumns: '1.25fr 1fr', gap: 18 }}>
        {/* ---- Needs attention ---- */}
        <div className="card">
          <div className="card-head">
            <div>
              <h2 className="card-title">Needs attention</h2>
              <p className="card-sub">Work queued for the platform team</p>
            </div>
          </div>
          <div>
            <AttentionRow
              icon={Tray}
              tone={registrations.open > 0 ? 'warn' : 'ok'}
              title="Registrations to review"
              sub={
                registrations.open > 0
                  ? 'Agencies waiting to hear back'
                  : 'Nothing waiting. The queue is clear'
              }
              count={registrations.open}
              onClick={() => navigate('/registrations')}
            />
            <AttentionRow
              icon={Prohibit}
              tone={agencies.inactive > 0 ? 'bad' : 'ok'}
              title="Suspended agencies"
              sub="Cannot sign in until reinstated"
              count={agencies.inactive}
              onClick={() => navigate('/agencies')}
              last
            />
          </div>
        </div>

        {/* ---- Network ---- */}
        <div className="card">
          <div className="card-head">
            <div>
              <h2 className="card-title">Across the network</h2>
              <p className="card-sub">What every agency has built, combined</p>
            </div>
          </div>
          <div className="card-pad stack" style={{ gap: 16 }}>
            <NetworkRow icon={House} label="Properties" value={network.properties} />
            <NetworkRow icon={UsersThree} label="Clients" value={network.customers} />
            <NetworkRow icon={Handshake} label="Deals" value={network.deals} />
            <p className="t-xs faint" style={{ margin: 0, lineHeight: 1.6 }}>
              Counted across all tenants. Per-agency numbers are on each agency&apos;s page.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-2" style={{ gap: 18 }}>
        {/* ---- Recent registrations ---- */}
        <div className="card">
          <div className="card-head">
            <div>
              <h2 className="card-title">Latest registrations</h2>
              <p className="card-sub">Newest requests first</p>
            </div>
            <div className="spacer" />
            <Link className="btn btn-sm" to="/registrations">
              View all
            </Link>
          </div>
          {recentRegistrations.length === 0 ? (
            <EmptyState title="No registrations yet" message="New requests will appear here." />
          ) : (
            <div>
              {recentRegistrations.map((registration) => (
                <div
                  key={registration._id}
                  className="table-row"
                  style={{ gridTemplateColumns: '1fr auto' }}
                >
                  <div className="row" style={{ minWidth: 0 }}>
                    <Avatar name={registration.agencyName} size={34} />
                    <div style={{ minWidth: 0 }}>
                      <p className="truncate strong" style={{ margin: 0, fontSize: 13 }}>
                        {registration.agencyName}
                      </p>
                      <p className="truncate t-xs muted" style={{ margin: '2px 0 0' }}>
                        {registration.contactName}
                        {registration.city ? ` · ${registration.city}` : ''}
                      </p>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <Badge
                      tone={
                        registration.status === 'new'
                          ? 'warn'
                          : registration.status === 'approved'
                            ? 'ok'
                            : registration.status === 'rejected'
                              ? 'bad'
                              : 'info'
                      }
                    >
                      {registration.status}
                    </Badge>
                    <p className="t-xs faint" style={{ margin: '4px 0 0' }}>
                      {relativeDate(registration.createdAt)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ---- Recent agencies ---- */}
        <div className="card">
          <div className="card-head">
            <div>
              <h2 className="card-title">Newest agencies</h2>
              <p className="card-sub">Most recently onboarded</p>
            </div>
            <div className="spacer" />
            <Link className="btn btn-sm" to="/agencies">
              View all
            </Link>
          </div>
          {recentAgencies.length === 0 ? (
            <EmptyState
              title="No agencies yet"
              message="Approve a registration, or onboard one directly."
            />
          ) : (
            <div>
              {recentAgencies.map((agency) => (
                <Link
                  key={agency._id}
                  to={`/agencies/${agency._id}`}
                  className="table-row"
                  style={{ gridTemplateColumns: '1fr auto', color: 'inherit' }}
                >
                  <div className="row" style={{ minWidth: 0 }}>
                    <Avatar name={agency.name} size={34} />
                    <div style={{ minWidth: 0 }}>
                      <p className="truncate strong" style={{ margin: 0, fontSize: 13 }}>
                        {agency.name}
                      </p>
                      <p className="truncate t-xs muted" style={{ margin: '2px 0 0' }}>
                        {agency.contactName || agency.phone}
                      </p>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div className="row row-tight" style={{ justifyContent: 'flex-end' }}>
                      <StatusDot tone={agency.status === 'active' ? 'ok' : 'bad'} />
                      <span className="t-sm strong">
                        {agency.status === 'active' ? 'Active' : 'Suspended'}
                      </span>
                    </div>
                    <p className="t-xs faint" style={{ margin: '4px 0 0' }}>
                      {relativeDate(agency.createdAt)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AttentionRow({
  icon: IconCmp,
  tone,
  title,
  sub,
  count,
  onClick,
  last,
}: {
  icon: Icon;
  tone: Tone;
  title: string;
  sub: string;
  count: number;
  onClick: () => void;
  last?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className="table-row"
      style={{
        gridTemplateColumns: '1fr auto',
        width: '100%',
        background: 'none',
        border: 'none',
        borderBottom: last ? 'none' : '1px solid var(--line-4)',
        cursor: 'pointer',
        textAlign: 'left',
        font: 'inherit',
      }}
    >
      <div className="row" style={{ minWidth: 0 }}>
        <div
          className="icon-tile"
          style={{ background: `var(--${tone}-bg)`, color: `var(--${tone})` }}
        >
          <IconCmp size={18} />
        </div>
        <div style={{ minWidth: 0 }}>
          <p className="truncate strong" style={{ margin: 0, fontSize: 13.5 }}>
            {title}
          </p>
          <p className="truncate t-xs muted" style={{ margin: '2px 0 0' }}>
            {sub}
          </p>
        </div>
      </div>
      <div className="row row-tight">
        {count === 0 ? (
          <CheckCircle size={19} color="var(--ok)" weight="fill" />
        ) : (
          <span className="num" style={{ fontSize: 19 }}>
            {count}
          </span>
        )}
        <ArrowRight size={14} color="var(--faint)" />
      </div>
    </button>
  );
}

function NetworkRow({ icon: IconCmp, label, value }: { icon: Icon; label: string; value: number }) {
  return (
    <div className="row">
      <div className="icon-tile" style={{ width: 34, height: 34 }}>
        <IconCmp size={17} />
      </div>
      <span className="t-md">{label}</span>
      <div className="spacer" />
      <span className="num" style={{ fontSize: 18 }}>
        {value.toLocaleString('en-IN')}
      </span>
    </div>
  );
}
